import { puzzelLogin } from "api/puzzel/puzzel";
import {
  ensurePuzzelAuthenticated,
  redirectToPuzzelLogin,
} from "app/PuzzelAuth/puzzelAuth";
import { isPuzzelOidcEnabled } from "app/PuzzelAuth/puzzelAuthConfig";
import {
  getPuzzelCustomerKey,
  resetPuzzelLoginAttempts,
} from "app/PuzzelAuth/puzzelTokenStorage";
import axios from "axios";
import { log, LogLevel } from "logging/datadogBrowserLogs";
import { devLog } from "shared/utils/devLog";

const PUZZEL_BASE_URL = "https://api.puzzel.com/ContactCentre5/";

// Legacy silent-login token storage keys - remove them both once puzzelOidcLogin is rolled out to everyone.
export const PUZZEL_ACCESS_TOKEN = "PUZZEL_ACCESS_TOKEN";
export const PUZZEL_CUSTOMER_KEY = "PUZZEL_CUSTOMER_KEY";

const puzzelHttpClient = axios.create();
const logSource = "puzzelHttpClient";

puzzelHttpClient.interceptors.request.use(async (config) => {
  if (isPuzzelOidcEnabled()) {
    const accessToken = await ensurePuzzelAuthenticated();
    // Customer key comes from the access jwt token's "urn:puzzel:cc:slid" claim (see savePuzzelTokens).
    // eslint-disable-next-line no-param-reassign -- Interceptor function
    config.url = `${PUZZEL_BASE_URL + getPuzzelCustomerKey()}/${config.url}`;
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = `Bearer ${accessToken}`;
    return config;
  }

  // eslint-disable-next-line no-param-reassign -- Interceptor function
  config.url = `${
    PUZZEL_BASE_URL + localStorage.getItem(PUZZEL_CUSTOMER_KEY)
  }/${config.url}`;
  // eslint-disable-next-line no-param-reassign
  config.headers.Authorization = `Bearer ${localStorage.getItem(
    PUZZEL_ACCESS_TOKEN,
  )}`;
  return config;
});

puzzelHttpClient.interceptors.response.use(
  (response) => {
    if (isPuzzelOidcEnabled()) {
      // Proves the token isn't just present but actually valid/authorized for this API.
      resetPuzzelLoginAttempts();
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      if (isPuzzelOidcEnabled()) {
        await redirectToPuzzelLogin();
        return new Promise(() => {});
      }
      const loginResponse = await puzzelLogin();
      localStorage.setItem(
        PUZZEL_ACCESS_TOKEN,
        loginResponse.data?.accessToken,
      );
      return puzzelHttpClient(originalRequest);
    }
    devLog(
      LogLevel.error,
      logSource,
      "Puzzel API error response body",
      error.response?.data,
    );
    log(
      LogLevel.error,
      logSource,
      `Call to ${error.response?.request?.responseURL} failed with status ${error.response?.status}: ${error.message}`,
    );
    return error;
  },
);

export { puzzelHttpClient };
